/**
 * UUIDv7 — time-ordered primary keys, generated in application code.
 *
 * PostgreSQL 17 has no native `uuidv7()`; it arrives in 18 (plan conflict C12). The
 * DB default stays `gen_random_uuid()` for system-inserted rows where ordering does
 * not matter.
 */
/** `0x00` … `0xff` as two hex digits, built once rather than per byte. */
const HEX = Array.from({ length: 256 }, (_, i) => i.toString(16).padStart(2, "0"));
/**
 * Generate a UUIDv7 — time-ordered, so primary keys cluster by insertion and B-tree
 * pages stay dense.
 *
 * Layout per RFC 9562 §5.7: 48-bit big-endian Unix epoch milliseconds, 4-bit
 * version, 12 random bits, 2-bit variant, 62 random bits.
 *
 * **Web Crypto, not `node:crypto`.** This function sits in the one entry point every
 * app imports, including `apps/app`, which is a browser bundle from Phase 2. The
 * original used `randomBytes` and `Buffer.toString("hex")` — correct, and a bundling
 * failure waiting for the dashboard to exist. `crypto.getRandomValues` is a global in
 * both runtimes and needs no import, so `packages/shared` keeps a clean module graph;
 * the hex encoding is done by hand for the same reason. A dependency-cruiser rule
 * now forbids Node core imports from this package so the regression cannot return
 * quietly.
 *
 * @param now - epoch milliseconds; injected so callers can generate deterministic
 *              ids in tests.
 */
export function uuidv7(now = Date.now()) {
    const bytes = new Uint8Array(16);
    globalThis.crypto.getRandomValues(bytes);
    // 48-bit timestamp, big-endian. Division rather than `>>>` above bit 31, because
    // the shift operators coerce to int32 and would silently drop the high bits.
    bytes[0] = (now / 2 ** 40) & 0xff;
    bytes[1] = (now / 2 ** 32) & 0xff;
    bytes[2] = (now / 2 ** 24) & 0xff;
    bytes[3] = (now / 2 ** 16) & 0xff;
    bytes[4] = (now / 2 ** 8) & 0xff;
    bytes[5] = now & 0xff;
    // Version 7 in the high nibble of byte 6; low nibble stays random.
    bytes[6] = (bytes[6] & 0x0f) | 0x70;
    // RFC 4122 variant in the top two bits of byte 8.
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    let hex = "";
    for (const byte of bytes)
        hex += HEX[byte];
    return [
        hex.slice(0, 8),
        hex.slice(8, 12),
        hex.slice(12, 16),
        hex.slice(16, 20),
        hex.slice(20, 32),
    ].join("-");
}
//# sourceMappingURL=uuid.js.map